/**
 * Typed URL search parameters shared by analytics screens (TZ 8.13 "URL'da filtrlar (davr, do'konlar) tiplangan
 * holda. Havola ulashilsa, xuddi shu ko'rinish ochiladi"). Unknown or malformed values fall back to defaults.
 */
import {
  isComparisonMode,
  isQuickRangeKey,
  isTradingDateString,
  normalizeCustomRange,
  resolveQuickRange,
  type ComparisonMode,
  type PeriodSelection,
  type QuickRangeKey,
} from '@person-platform/ui';
import { METRIC_KEYS, type MetricKey } from '../api/api_types';
import type { RankingPeriod } from '../api/client_endpoints';

export interface AnalyticsSearch {
  period?: QuickRangeKey;
  from?: string;
  to?: string;
  compare?: ComparisonMode;
  /** Comma-separated store ids; absent = all visible stores. */
  stores?: string;
}

const STORE_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export function validateAnalyticsSearch(rawSearch: Record<string, unknown>): AnalyticsSearch {
  const validatedSearch: AnalyticsSearch = {};
  if (isQuickRangeKey(rawSearch.period)) {
    validatedSearch.period = rawSearch.period;
  }
  if (isTradingDateString(rawSearch.from)) {
    validatedSearch.from = rawSearch.from;
  }
  if (isTradingDateString(rawSearch.to)) {
    validatedSearch.to = rawSearch.to;
  }
  if (isComparisonMode(rawSearch.compare)) {
    validatedSearch.compare = rawSearch.compare;
  }
  if (typeof rawSearch.stores === 'string') {
    const storeIds = rawSearch.stores.split(',').filter((storeId) => STORE_ID_PATTERN.test(storeId));
    if (storeIds.length > 0) {
      validatedSearch.stores = storeIds.join(',');
    }
  }
  return validatedSearch;
}

/** Period of the screen: custom ranges keep their dates, presets are resolved against "today" (live links). */
export function resolvePeriodSelection(
  search: AnalyticsSearch,
  today: string,
  defaultPreset: QuickRangeKey,
): PeriodSelection {
  const preset = search.period ?? defaultPreset;
  if (preset === 'custom') {
    if (search.from !== undefined && search.to !== undefined) {
      return { preset: 'custom', ...normalizeCustomRange({ from: search.from, to: search.to }, today) };
    }
    return { preset: 'today', ...resolveQuickRange('today', today) };
  }
  return { preset, ...resolveQuickRange(preset, today) };
}

export function periodSelectionToSearch(
  periodSelection: PeriodSelection,
): Pick<AnalyticsSearch, 'period' | 'from' | 'to'> {
  return periodSelection.preset === 'custom'
    ? { period: 'custom', from: periodSelection.from, to: periodSelection.to }
    : { period: periodSelection.preset, from: undefined, to: undefined };
}

/** Selected store ids limited to the stores the user may see (store users never get others, SEC-08). */
export function selectedStoreIds(search: AnalyticsSearch, visibleStoreIds: readonly string[]): string[] {
  if (search.stores === undefined) {
    return [...visibleStoreIds];
  }
  const requestedIds = search.stores.split(',');
  const allowedIds = requestedIds.filter((storeId) => visibleStoreIds.includes(storeId));
  return allowedIds.length === 0 ? [...visibleStoreIds] : allowedIds;
}

export function storeIdsToSearch(
  storeIds: readonly string[],
  visibleStoreIds: readonly string[],
): string | undefined {
  const coversAll =
    storeIds.length === visibleStoreIds.length &&
    visibleStoreIds.every((storeId) => storeIds.includes(storeId));
  return storeIds.length === 0 || coversAll ? undefined : storeIds.join(',');
}

export function isMetricKey(candidate: unknown): candidate is MetricKey {
  return typeof candidate === 'string' && (METRIC_KEYS as readonly string[]).includes(candidate);
}

/** 8.4 comparison views. */
export const COMPARISON_VIEWS = ['line', 'indexed', 'small_multiples', 'weekday', 'table'] as const;
export type ComparisonView = (typeof COMPARISON_VIEWS)[number];
export const COMPARISON_MODES_OF_STORES = ['stores', 'network'] as const;
export type StoresComparisonMode = (typeof COMPARISON_MODES_OF_STORES)[number];
/** 8.4 metric switch. `returning_share` is displayed as a share of visits. */
export const COMPARISON_METRICS = [
  'unique',
  'visits',
  'visits_per_hour',
  'returning_share',
  'children',
  'cancelled',
  'work_hours',
] as const;
export type ComparisonMetric = (typeof COMPARISON_METRICS)[number];

export interface ComparisonSearch extends AnalyticsSearch {
  metric?: ComparisonMetric;
  view?: ComparisonView;
  mode?: StoresComparisonMode;
}

function includesValue<Value extends string>(
  allowedValues: readonly Value[],
  candidate: unknown,
): candidate is Value {
  return typeof candidate === 'string' && (allowedValues as readonly string[]).includes(candidate);
}

export function validateComparisonSearch(rawSearch: Record<string, unknown>): ComparisonSearch {
  return {
    ...validateAnalyticsSearch(rawSearch),
    metric: includesValue(COMPARISON_METRICS, rawSearch.metric) ? rawSearch.metric : undefined,
    view: includesValue(COMPARISON_VIEWS, rawSearch.view) ? rawSearch.view : undefined,
    mode: includesValue(COMPARISON_MODES_OF_STORES, rawSearch.mode) ? rawSearch.mode : undefined,
  };
}

export const RANKING_PERIODS: readonly RankingPeriod[] = [
  'day',
  'week',
  'month',
  'quarter',
  'year',
  'custom',
];
/**
 * 8.5: ranking metrics. The default is unique visitors (owner decision 2026-10-05, over TZ 8.5's visits per hour);
 * visits per working hour stays one click away for a fair comparison of stores with different hours.
 */
export const RANKING_METRICS: readonly MetricKey[] = [
  'unique',
  'visits',
  'visits_per_hour',
  'returning',
  'children',
  'cancelled',
  'work_hours',
];
/** The metric that is fair to stores with different working hours (8.5). */
export const FAIR_RANKING_METRIC: MetricKey = 'visits_per_hour';

export interface RankingSearch {
  metric?: MetricKey;
  rank_period?: RankingPeriod;
  from?: string;
  to?: string;
  t_sort?: string;
  t_q?: string;
  t_hide?: string;
}

export function validateRankingSearch(rawSearch: Record<string, unknown>): RankingSearch {
  return {
    metric:
      isMetricKey(rawSearch.metric) && RANKING_METRICS.includes(rawSearch.metric)
        ? rawSearch.metric
        : undefined,
    rank_period: includesValue(RANKING_PERIODS, rawSearch.rank_period) ? rawSearch.rank_period : undefined,
    from: isTradingDateString(rawSearch.from) ? rawSearch.from : undefined,
    to: isTradingDateString(rawSearch.to) ? rawSearch.to : undefined,
    t_sort: typeof rawSearch.t_sort === 'string' ? rawSearch.t_sort : undefined,
    t_q: typeof rawSearch.t_q === 'string' ? rawSearch.t_q : undefined,
    t_hide: typeof rawSearch.t_hide === 'string' ? rawSearch.t_hide : undefined,
  };
}

export interface TableSearch extends AnalyticsSearch {
  t_sort?: string;
  t_q?: string;
  t_hide?: string;
}

export function validateTableSearch(rawSearch: Record<string, unknown>): TableSearch {
  return {
    ...validateAnalyticsSearch(rawSearch),
    t_sort: typeof rawSearch.t_sort === 'string' ? rawSearch.t_sort : undefined,
    t_q: typeof rawSearch.t_q === 'string' ? rawSearch.t_q : undefined,
    t_hide: typeof rawSearch.t_hide === 'string' ? rawSearch.t_hide : undefined,
  };
}

export interface LoginSearch {
  redirect?: string;
}

/** Only same-app relative paths are accepted as a post-login redirect (no open redirect). */
export function validateLoginSearch(rawSearch: Record<string, unknown>): LoginSearch {
  const redirectTarget = rawSearch.redirect;
  return typeof redirectTarget === 'string' &&
    redirectTarget.startsWith('/') &&
    !redirectTarget.startsWith('//')
    ? { redirect: redirectTarget }
    : {};
}

/** 8.6 trends: the KPI tile click opens the trend of that metric. */
export interface TrendsSearch extends AnalyticsSearch {
  metric?: MetricKey;
}

export function validateTrendsSearch(rawSearch: Record<string, unknown>): TrendsSearch {
  return {
    ...validateAnalyticsSearch(rawSearch),
    metric: isMetricKey(rawSearch.metric) ? rawSearch.metric : undefined,
  };
}
